import React, { useState, useMemo, useCallback, useEffect, forwardRef } from "react";

import {
  MaterialReactTable
} from "material-react-table";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import AddIcon from "@mui/icons-material/Add";
import { BeatLoader } from "react-spinners";
import Loader from '../../Components/Loader/Loader';
import ErrorComponent from "../ErrorComponent/ErrorComponent";
import Popover from "@mui/material/Popover";
import { useSelector, useDispatch } from "react-redux";
import { openDrawer } from "../../actions/drawerActions";
import { DeletePortfolio } from "../../Apis/DeletePortfolio";
import { fetchPortfolio, togglePortfolioSelection, toggleClientSelection, togglePortfolioHedgeableFlag,selectPortfolio, selectClient, toggleSelectedPortfolioDetails } from "../../actions/portfolioActions";
import { addZeroes } from "../../Utilities/AddZeros";
import { customColumnSort } from "../../Utilities/CustomColumnSort";
import {
  fetchPortfolioDetailsIfNeeded,
  fetchPortfolioDetails,
  addTicker,
  deleteTicker,
} from "../../actions/portfolioDetailsActions";
import {
  handleApiError,
  showConfirmNotification,
} from "../../actions/notificationActions";
import Toaster from "../../Components/Toaster/Toaster";
import TagManager from "react-gtm-module";
import { clearPdfSummaryData } from '../../actions/pdfSummaryActions';
import AutoCompleteTicker from "../AutoCompleteTicker/AutoCompleteTicker";
import { fetchOptionsChainTickers } from "../../actions/optionChainTickersAction";
import SkeletonComponent from '../Skeleton/Skeleton';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OptionExpirationDatePicker from "../OptionExpirationDatePicker/OptionExpirationDatePicker";

function PortfolioDetails({ portfolioId, portfolioName, hasSchwabAccount, portfolio, customer, allCustomers, hedgeableFlag, archiveStatusFlag }) {
  var currObj = {
    style: "currency",
    currency: "USD",
  };
  const dispatch = useDispatch();

  const [loadingState, setLoadingState] = useState({
    addTicker: false,
    deleteTicker: false,
  });
  const [ticker, setTicker] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [toggleOptionTickerFlag, setToggleOptionTickerFlag] = useState(false);
  const [isOptionTickerSelected, setIsOptionTickerSelected] = useState(false);
  const [optionTicker, setOptionTicker] = useState(null);
  const [contractType, setContractType] = useState("");
  const [strikePrice, setStrikePrice] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const portfolioDetails = useSelector(
    (state) => state.portfolioDetails[portfolioId]
  );
  const activeDrawer = useSelector(state => state.drawer.activeDrawer);
  const portfolioDetailsState = useSelector(state => state.portfolioDetails);
  const stockTickers = useSelector(state => state.availableTickers.availableTickers);
  const optionTickers = useSelector(state => state.availableOptionsTickers.availableOptionsTickers);
  const optionChainTickers = useSelector(state => state.optionChainTickers);

  const availableTickers = useMemo(() => {
    return toggleOptionTickerFlag ? optionTickers : stockTickers;
  }, [toggleOptionTickerFlag, optionTickers, stockTickers]);

  const [pendingDrawer, setPendingDrawer] = useState(null);
  const [loading, setLoading] = useState({});
  const [openPortfolios, setOpenPortfolios] = useState({});
  const [csvData, setCSVData] = useState(false)
  const subscriptionsDetails = useSelector(state => state.userSubscription.subscriptionData) || {};
  const { user_type: userType, customer_id: CustomerID } = subscriptionsDetails;
  const showAction = portfolio.Hedgable > subscriptionsDetails.subscription_plan_active_portfolio_line_items_limit;
  const [showAlmostThere, setShowAlmostThere] = useState(false);

  const isOptionTickerValid = optionTicker && typeof optionTicker === 'string' && optionTicker.trim() !== '';

  const selectedTickerOptionData = useMemo(() => {
    if (!optionTicker || !optionChainTickers.optionsChainTickers[optionTicker]) {
      return [];
    }
    return optionChainTickers.optionsChainTickers[optionTicker] || [];
  }, [optionTicker, optionChainTickers.optionsChainTickers]);

  const availableContractTypes = useMemo(() => {
    if (!selectedTickerOptionData.length) return [];
    const contractTypes = [...new Set(selectedTickerOptionData.map(option => option.call_put))];
    return contractTypes.sort();
  }, [selectedTickerOptionData]);

  const availableExpirationDates = useMemo(() => {
    if (!selectedTickerOptionData.length || !contractType) return [];
    const expirationDates = selectedTickerOptionData
      .filter(option => option.call_put === contractType)
      .map(option => option.expiration_date)
      .filter((date, index, arr) => arr.indexOf(date) === index)
      .sort();
    return expirationDates;
  }, [selectedTickerOptionData, contractType]);

  const isQuantityFieldDisabled = useMemo(() => {
    if (errorMessage && errorMessage.trim() !== "") {
      const quantityRelatedErrors = [
        "Please enter a valid number.",
        "Quantity cannot be zero.",
        "Please enter a quantity greater than 0.",
        "Quantity field is required",
        "Quantity field cannot be empty"
      ];
      return !quantityRelatedErrors.some(error => errorMessage.includes(error));
    }
    
    if (toggleOptionTickerFlag) {
      return !isOptionTickerSelected || 
             !isOptionTickerValid || 
             !contractType || 
             !strikePrice || 
             !expirationDate ||
             optionChainTickers.isLoadingChain;
    } else {
      return !ticker;
    }
  }, [errorMessage, toggleOptionTickerFlag, isOptionTickerSelected, isOptionTickerValid, 
      contractType, strikePrice, expirationDate, optionChainTickers.isLoadingChain, ticker]);

  const availableStrikePrices = useMemo(() => {
    if (!selectedTickerOptionData.length || !contractType || !expirationDate) return [];
    const strikePrices = selectedTickerOptionData
      .filter(option => 
        option.call_put === contractType && 
        option.expiration_date === expirationDate
      )
      .map(option => option.strike_price)
      .filter((price, index, arr) => arr.indexOf(price) === index)
      .sort((a, b) => parseFloat(a) - parseFloat(b));
    return strikePrices;
  }, [selectedTickerOptionData, contractType, expirationDate]);

  React.useEffect(() => {
      let timer;
      if (portfolioDetails.isLoading || loadingState.addTicker || loadingState.deleteTicker) {
          timer = setTimeout(() => {setShowAlmostThere(true);}, 1750);
      } else {
          setShowAlmostThere(false);
      }
      return () => clearTimeout(timer);
  }, [portfolioDetails.isLoading, loadingState]);

  React.useEffect(() => {
    setErrorMessage("");
    setQuantity("");
    
    if (!toggleOptionTickerFlag) {
      setOptionTicker(null);
      setIsOptionTickerSelected(false);
      setContractType("");
      setStrikePrice("");
      setExpirationDate("");
    } else {
      setTicker(null);
    }
  }, [toggleOptionTickerFlag]);

  React.useEffect(() => {
    if (contractType && availableExpirationDates.length > 0) {
      if (!availableExpirationDates.includes(expirationDate)) {
        setExpirationDate("");
        setStrikePrice("");
      }
    }
  }, [contractType, availableExpirationDates, expirationDate]);

  React.useEffect(() => {
    if (expirationDate && availableStrikePrices.length > 0) {
      if (!availableStrikePrices.includes(parseFloat(strikePrice))) {
        setStrikePrice("");
      }
    }
  }, [expirationDate, availableStrikePrices, strikePrice]);


  const toggleTable = (portfolioId) => {
    setOpenPortfolios(prevOpenPortfolios => ({
        ...prevOpenPortfolios,
        [portfolioId]: !prevOpenPortfolios[portfolioId]
    }));
  };

  const handleTickerChange = (e,newValue) => {
    const referenceTicker = availableTickers.find(
      (refTicker) => refTicker.symbol === newValue.symbol.toUpperCase()
    );
    if (!referenceTicker) {
      setErrorMessage("Symbol not recognized");
    } else {
      setErrorMessage("");
      setTicker(newValue.symbol.toUpperCase());
    }
  };

  const handleOptionTickerChange = (e, newValue) => {
    const resetOptionFormState = () => {
      setOptionTicker(null);
      setIsOptionTickerSelected(false);
      setContractType("");
      setStrikePrice("");
      setExpirationDate("");
      setErrorMessage("");
    };

    if (!newValue?.symbol) {
      resetOptionFormState();
      return;
    }

    const tickerSymbol = newValue.symbol.toUpperCase();
    const referenceTicker = availableTickers.find(
      (refTicker) => refTicker.symbol === tickerSymbol
    );

    if (!referenceTicker) {
      setErrorMessage("Symbol not recognized");
      setIsOptionTickerSelected(false);
      setOptionTicker(null);
      setContractType("");
      setStrikePrice("");
      setExpirationDate("");
      return;
    }

    setErrorMessage("");
    setOptionTicker(tickerSymbol);
    setIsOptionTickerSelected(true);
    
    if (!optionChainTickers.optionsChainTickers[tickerSymbol]) {
      dispatch(fetchOptionsChainTickers(tickerSymbol));
    }
  };

  const open = Boolean(anchorEl);
  const handleActionClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    const quantityNum = parseFloat(value);
    let newErrorMessage = "";
  
    if (value && isNaN(quantityNum)) {
      newErrorMessage = "Please enter a valid number.";
    } else if (quantityNum === 0) {
      newErrorMessage = "Quantity cannot be zero.";
    }
    setQuantity(value);
    setErrorMessage(newErrorMessage);
  };  

  const handleAddTicker = () => {
    const quantityNum = parseFloat(quantity);
    const hedgeableItems = portfolioDetailsState[portfolioId]?.details?.PortfolioLineItem?.Hedgeable || [];
    if (!ticker && !quantityNum) {
      setErrorMessage("Symbol and quantity field cannot be empty");
      return;
    }
    if (isNaN(quantityNum) || quantityNum <= 0) {
      setErrorMessage("Please enter a quantity greater than 0.");
      return;
    }
    if (!ticker) {
      setErrorMessage("Symbol field cannot be empty");
      return;
    }
    if (hedgeableItems.some(item => item.Ticker === ticker)) {
      setErrorMessage("Symbol is already present in the Portfolio");
      return;
    }
    const limit = subscriptionsDetails.subscription_plan_active_portfolio_line_items_limit;
    if (limit !== 'Infinity' && hedgeableItems.length >= limit) {
      setErrorMessage(`Your current portfolio line items count (${hedgeableItems.length}) exceeds the limit of ${limit}. Please upgrade your subscription to add more.`);
      return;
    }
    setLoadingState((prev) => ({ ...prev, addTicker: true }));
    dispatch(addTicker(portfolioId, ticker, quantity))
    .then(() => {
      dispatch(fetchPortfolio());
    })
    .finally(() => {
      setLoadingState((prev) => ({ ...prev, addTicker: false }));
      setTicker("");
      setQuantity("");
      setErrorMessage("");
      TagManager.dataLayer({
        dataLayer: {
          event: "Add Ticker",
          userType: userType,
          user_id: CustomerID,
        },
      });
    });
  };

  const handleAddOptionTicker = () => {
    const hedgeableItems = portfolioDetailsState[portfolioId]?.details?.PortfolioLineItem?.Hedgeable || [];
    const quantityNum = parseFloat(quantity);
    
    if (!optionTicker) {
      setErrorMessage("Symbol field is required");
      return;
    }
    
    if (!contractType) {
      setErrorMessage("Contract Type is required");
      return;
    }
    
    if (!strikePrice) {
      setErrorMessage("Strike Price is required");
      return;
    }
    
    if (!expirationDate) {
      setErrorMessage("Expiration Date is required");
      return;
    }
    
    if (!quantity) {
      setErrorMessage("Quantity field is required");
      return;
    }
    
    if (isNaN(quantityNum) || quantityNum <= 0) {
      setErrorMessage("Please enter a quantity greater than 0.");
      return;
    }
    
    const matchingOption = selectedTickerOptionData.find(option => 
      option.call_put === contractType && 
      option.strike_price === parseFloat(strikePrice) && 
      option.expiration_date === expirationDate
    );
    
    if (!matchingOption || !matchingOption.opt_sec_id) {
      setErrorMessage("Unable to find the selected option contract. Please try again.");
      return;
    }
    const opt_sec_id = matchingOption.opt_sec_id;
    if (hedgeableItems.some(item => item.Ticker === opt_sec_id)) {
      setErrorMessage("This specific option contract is already present in the Portfolio");
      return;
    }
    
    const limit = subscriptionsDetails.subscription_plan_active_portfolio_line_items_limit;
    if (limit !== 'Infinity' && hedgeableItems.length >= limit) {
      setErrorMessage(`Your current portfolio line items count (${hedgeableItems.length}) exceeds the limit of ${limit}. Please upgrade your subscription to add more.`);
      return;
    }
    
    setLoadingState((prev) => ({ ...prev, addTicker: true }));
    dispatch(addTicker(portfolioId, opt_sec_id, quantity))
    .then(() => {
      dispatch(fetchPortfolio());
    })
    .finally(() => {
      setLoadingState((prev) => ({ ...prev, addTicker: false }));
      setOptionTicker(null);
      setIsOptionTickerSelected(false);
      setQuantity("");
      setContractType("");
      setStrikePrice("");
      setExpirationDate("");
      setErrorMessage("");
      TagManager.dataLayer({
        dataLayer: {
          event: "Add Option Ticker",
          userType: userType,
          user_id: CustomerID,
        },
      });
    });
  };

  const handleDeleteTicker = (ticker) => {
    setLoadingState((prev) => ({ ...prev, deleteTicker: true }));
    dispatch(deleteTicker(portfolioId, ticker))
    .then(() => {
      dispatch(fetchPortfolio());
    })
    .finally(() => {
      setLoadingState((prev) => ({ ...prev, deleteTicker: false }));
      TagManager.dataLayer({
        dataLayer: {
          event: "Delete Ticker",
          userType: userType,
          user_id: CustomerID,
        },
      });
    });
  };

  const hedgeableColumns = useMemo(
    () => [
      {
        accessorKey: "Ticker",
        header: "SYMBOL",
        size: 100,
      },
      {
        accessorKey: "CompanyName",
        header: "DESCRIPTION",
        size: 250,
      },
      {
        accessorKey: "Quantity",
        header: "QUANTITY",
        sorting: true,
        sortingFn: (a,b) => customColumnSort(a,b,'Quantity')
      },
      {
        accessorKey: "ClosePrice",
        header: "PRICE",
        sorting: true,
        sortingFn: (a,b) => customColumnSort(a,b,'ClosePrice')
        
      },
      {
        accessorKey: "TotalValue",
        header: "MARKET VALUE",
        sorting: true,
        sortingFn: (a, b) => customColumnSort(a, b,'TotalValue')
        
      },
      {
        accessorKey: "MarketWeight",
        header: "MARKET WEIGHT",
        sorting:true,
        sortingFn:(a,b) => customColumnSort(a,b,'MarketWeight'),
        size: 160,
        
      },
      {
        accessorKey: "actions",
        header: "ACTIONS",
        size: 110,
        Cell: ({ row }) => {
          if (!hasSchwabAccount) {
            if(archiveStatusFlag || showAction){
              return (
                <button
                  className="deleterow"
                  onClick={() => handleDeleteTicker(row.original.Ticker)}
                >
                  <img src="./Assets/trash.svg" alt="Delete" />
                </button>
              );
            }
          }
          return null;
        },
      },
    ],
    [hasSchwabAccount,archiveStatusFlag,showAction]
  );

  const unhedgeableColumns = useMemo(
    () => [
      {
        accessorKey: "Ticker",
        header: "SYMBOL",
        size: 100,
      },
      {
        accessorKey: "Quantity",
        header: "QUANTITY",
        sorting: true,
        sortingFn: (a,b) => customColumnSort(a,b,'Quantity')
      },
    ],
    []
  );

  const data = useMemo(() => {
    if (!portfolioDetails?.details?.PortfolioLineItem) {
      return [];
    }
    const portfolioLineItems = hedgeableFlag
      ? portfolioDetails.details.PortfolioLineItem.Unhedgeable
      : portfolioDetails.details.PortfolioLineItem.Hedgeable;

    const totalPortfolioValue = parseFloat(
      portfolioDetails.details.TotalPortfolioValue
    );

    return portfolioLineItems.map((item) => ({
      Ticker: item.Ticker,
      CompanyName: item.CompanyName,
      Quantity: Number(addZeroes(Number(parseFloat(item.Quantity).toFixed(2)))).toLocaleString('US'),
      ClosePrice:
        item.ClosePrice === "-" ? "-" : Number(addZeroes(Number(parseFloat(item.ClosePrice).toFixed(2)))).toLocaleString('US',currObj),
      TotalValue: Number(addZeroes(Number(parseFloat(item.Quantity * item.ClosePrice).toFixed(2)))).toLocaleString('US',currObj),
      MarketWeight: `${(
        ((item.Quantity * item.ClosePrice) / totalPortfolioValue) *
        100
      ).toFixed(2)}%`,
    }));
  }, [portfolioDetails, hedgeableFlag]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePortfolioDelete = (portfolioid) => {
    handleClose();
    dispatch(
      showConfirmNotification(
        "Are you sure you want to delete this portfolio ?",
        () => confirmDelete(portfolioid)
      )
    );
  };

  const confirmDelete = async (portfolioid) => {
    setLoadingState((prev) => ({ ...prev, deleteTicker: true }));
    const data = await DeletePortfolio(portfolioid);
    if (data.status === 200) {
      TagManager.dataLayer({
        dataLayer: {
          event: "Portfolio Deleted",
          userType: userType,
          user_id: CustomerID,
        },
      });
      dispatch(fetchPortfolio());
    } else {
      handleApiError(data, dispatch);
    }
    setLoadingState((prev) => ({ ...prev, deleteTicker: false }));
  };

  const handlePortfolioClick = (portfolioId,action) => {
    dispatch(togglePortfolioSelection(portfolioId,action));
    dispatch(fetchPortfolioDetailsIfNeeded(portfolioId));
    dispatch(clearPdfSummaryData(portfolioId))
    toggleTable(portfolioId);
    //dispatch(selectPortfolio(portfolioId,action));
  };

  const openDrawerWithCheck = (drawerType, portfolio, customer) => {

    if (activeDrawer === drawerType) {
        return;
    }

    const loadingKey = `${portfolio.PortfolioId}-${drawerType}`;
    const details = portfolioDetailsState[portfolio.PortfolioId];

    const startLoading = () => setLoading(prev => ({ ...prev, [loadingKey]: true }));
    const stopLoading = () => setLoading(prev => ({ ...prev, [loadingKey]: false }));

    if (!details || details.needsUpdate || details.isLoading) {
        startLoading();
        dispatch(fetchPortfolioDetailsIfNeeded(portfolio.PortfolioId))
            .finally(stopLoading);
        setPendingDrawer({
            type: drawerType,
            portfolioId: portfolio.PortfolioId,
            name: `${customer.FirstName} ${customer.LastName}`,
            portfolioName: portfolio.PortfolioName
        });
    } else {
        const isEmpty = !(details.details?.PortfolioLineItem?.Hedgeable?.length > 0);
        dispatch(openDrawer(drawerType, {
            portfolioId: portfolio.PortfolioId,
            Name: `${customer.FirstName} ${customer.LastName}`,
            portfolio: portfolio.PortfolioName,
            isEmpty: isEmpty
        }));
    }
  };

  const handleDrawerClick = (drawer,portfolio,customer) =>{
    dispatch(toggleSelectedPortfolioDetails(customer.CustomerId, portfolio.PortfolioId))
    openDrawerWithCheck(drawer, portfolio, customer);
    handlePortfolioClick(portfolio.PortfolioId,'drawer');
    dispatch(selectClient(customer.CustomerId));
    //dispatch(selectPortfolio(portfolio.PortfolioId));
  }

  const handleCloneClick = (portfolio,customer) =>{
    dispatch(openDrawer('ClonePortfolio', { portfolioId: portfolio.PortfolioId, portfolio: portfolio.PortfolioName, customers: allCustomers, client: customer.FirstName + ' ' + customer.LastName }))
    handlePortfolioClick(portfolio.PortfolioId,'drawer');
  }

  const toggleHedgeableFlag = (portfolioId,CustomerId) => {
    dispatch(togglePortfolioHedgeableFlag(portfolioId,CustomerId));
  };

  const convertArrayToCSV = (array) => {
    const headers = Object.keys(array[0]).join(',');
    const rows = array.map(obj => Object.values(obj).join(',')).join('\n');
    return `${headers}\n${rows}`;
  };
  const handlePortfolioDownload = (e) => {
    e.preventDefault();
    let data = [];
    const { PortfolioLineItem, TotalPortfolioValue, PortfolioName } = portfolioDetails.details;
    for (const status in PortfolioLineItem) {
        const isHedgeable = status === 'Hedgeable';
        PortfolioLineItem[status].forEach(item => {
            const marketWeight = ((parseFloat(item.TotalValue) / parseFloat(TotalPortfolioValue)) * 100).toFixed(2);
            data.push({
                ticker: item.Ticker,
                company_name: item.CompanyName,
                price: item.ClosePrice,
                quantity: item.Quantity,
                market_weight: marketWeight,
                market_value: item.TotalValue,
                recognized: isHedgeable
            });
        });
    }
    if (data.length === 0) {
        console.error("No data to download");
        return;
    }
    setCSVData(data);
    const csv = convertArrayToCSV(data);
    const element = document.createElement("a");
    const file = new Blob([csv], { type: "text/csv;charset=utf-8" });
    element.href = URL.createObjectURL(file);
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    element.download = `${PortfolioName}_${formattedDate}.csv`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };
  return (
    <>
      <div className="toast-container">
        <Toaster />
      </div>
      {portfolioDetails.isLoading ||
      loadingState.addTicker ||
      loadingState.deleteTicker ? (
        <div className="details-table-container " style={{ height: "300px" }}>
          <div className="loading-overlay">
            <SkeletonComponent variant="portfoliolineitems" showAlmostThere={showAlmostThere} />
          </div>
        </div>
      ) : (
        <>
          <div className="portfolio-header-container">
            
          </div>
          <MaterialReactTable
            columns={hedgeableFlag?unhedgeableColumns:hedgeableColumns}
            data={data}
            enableColumnActions={false}
            enableDensityToggle={false}
            enableStickyHeader
            enableColumnResizing
            enableColumnOrdering
            enableRowPinning
            enablePagination={false}
            enableFullScreenToggle={false}
            muiTableBodyProps={{
              sx: {
                maxHeight: "300px", // Set the max height of the table body for scrolling
              },
            }}
            initialState={{
              density: "compact", // Set the initial density to 'compact'
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "white", // Adjust the header cell styling
                position: "sticky", // Stick the header
                top: 0, // Stick to the top
                zIndex: 1, // Ensure it’s above other content
              },
            }}
            renderTopToolbarCustomActions={() => (
              <div className="operations">
                <div className="switchcontent">
                  Hedgeable Assets
                  <div className="toggle-switch-small">
                    <input
                      type="checkbox"
                      id="mode-toggle"
                      checked={hedgeableFlag}
                      onChange={() => toggleHedgeableFlag(portfolioId,customer.CustomerId)}
                    ></input>
                    <label htmlFor="mode-toggle" className="toggle-label-small"></label>
                  </div>
                  Unhedgeable Assets
                </div>
                {archiveStatusFlag && (
                  <>
                    <button className="ai-btn solid secondary mhide" onClick={() => handleDrawerClick('Analytics', portfolio, customer)}>
                      <img src="Assets/graph.svg" alt="" /> Portfolio Analytics
                    </button>
                    <button className="ai-btn solid primary" onClick={() => handleDrawerClick('Recommendation', portfolio, customer)}>
                      <img src="Assets/shield.svg" alt="" /> Shield Recommendations
                    </button>
                    <div className="portfolio-details">
                      {!hasSchwabAccount && (
                        <button
                          className="pf-action-button"
                          onClick={(e) => handleActionClick(e)}
                        >
                          Actions <span className="arrow-down" />
                        </button>
                      )}
                    </div>
                  </>
                )}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <div className="popoverContainer">
                    <button
                        onClick={() => {
                            dispatch(openDrawer('EditPortfolio', { portfolioDetails: portfolioDetails.details?.PortfolioLineItem?.Hedgeable?.length > 0 ? portfolioDetails.details : { PortfolioId: portfolioId, PortfolioName: portfolioName } }));
                            handleClose();
                        }}
                        className="popoverButton"
                    >
                        <span>Edit Portfolio</span>
                        <img className="product_card_img" src='./Assets/Typing.svg' alt="Edit" />
                    </button>
                    <button
                        onClick={() => handlePortfolioDelete(portfolioId)}
                        className="popoverButton"
                    >
                        <span>Delete Portfolio</span>
                        <img className="product_card_img" src="./Assets/trash.svg" alt="Delete" />
                    </button>
                    <button
                        onClick={(e) => handlePortfolioDownload(e)}
                        className="popoverButton"
                    >
                        <span>Download Portfolio</span>
                        <img className="product_card_img" src="./Assets/Export.png" alt="Delete" />
                    </button>
                </div>
            </Popover>
                {/* <button className="ai-btn solid blue" onClick={() => handleCloneClick(portfolio,customer)}>
                  <img src="Assets/clone.svg" alt="" /> Clone Portfolio
                </button> */}
              </div>
            )}
            renderBottomToolbarCustomActions={() => (
              archiveStatusFlag && (
              <>
                <div className="title-add-ticker">
                  <h3>Add Position</h3>
                  <div className="switchcontent">
                    Stocks
                    <div className="toggle-switch-small">
                      <input
                        type="checkbox"
                        id="option-toggle"
                        checked={toggleOptionTickerFlag}
                        onChange={() => setToggleOptionTickerFlag(!toggleOptionTickerFlag)}
                      ></input>
                      <label htmlFor="option-toggle" className="toggle-label-small"></label>
                    </div>
                    Options
                  </div>
                {toggleOptionTickerFlag ? (
                  <div className="addOptionticker">
                    <AutoCompleteTicker
                        key="option-ticker"
                        onTickerChange={handleOptionTickerChange}
                        toggleOptionTickerFlag={toggleOptionTickerFlag}
                    />
                    <Select
                      value={contractType}
                      onChange={(e) => setContractType(e.target.value)}
                      displayEmpty
                      size="small"
                      disabled={!isOptionTickerSelected || !isOptionTickerValid || optionChainTickers.isLoadingChain}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                            '&::-webkit-scrollbar': {
                              width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: 'var(--Primary, #007bff)',
                              borderRadius: '3px',
                              '&:hover': {
                                background: 'var(--Primary, #0056b3)',
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>Contract Type</MenuItem>
                      {availableContractTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                    <OptionExpirationDatePicker
                      defaultDate={expirationDate}
                      onDateSelect={(date) => setExpirationDate(date)}
                      availableDates={availableExpirationDates}
                      disabled={!isOptionTickerSelected || !isOptionTickerValid || optionChainTickers.isLoadingChain || !contractType}
                      placeholder="Expiration Date"
                    />
                    <Select
                      value={strikePrice}
                      onChange={(e) => setStrikePrice(e.target.value)}
                      displayEmpty
                      size="small"
                      disabled={!isOptionTickerSelected || !isOptionTickerValid || optionChainTickers.isLoadingChain || !contractType || !expirationDate}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                            '&::-webkit-scrollbar': {
                              width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: 'var(--Primary, #007bff)',
                              borderRadius: '3px',
                              '&:hover': {
                                background: 'var(--Primary, #0056b3)',
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>Strike Price</MenuItem>
                      {availableStrikePrices.map((price) => (
                        <MenuItem key={price} value={price}>
                          ${price}
                        </MenuItem>
                      ))}
                    </Select>
                    <input
                      className="ai-input"
                      type="number"
                      placeholder="Quantity"
                      value={quantity}
                      onChange={handleQuantityChange}
                      step="any"
                      disabled={isQuantityFieldDisabled}
                    />
                    <button
                      variant="contained"
                      color="primary"
                      onClick={handleAddOptionTicker}
                      disabled={!isOptionTickerSelected || !isOptionTickerValid || optionChainTickers.isLoadingChain}
                    >
                      +
                    </button>
                    {optionChainTickers.isLoadingChain && (
                      <p className="option-loading-message">
                        Fetching Option Contracts...
                      </p>
                    )}
                  </div>
                ) : (
                  <div
                    className="addticker"
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                    }}
                  >
                    <AutoCompleteTicker
                        key="stock-ticker"
                        onTickerChange={handleTickerChange}
                        toggleOptionTickerFlag={toggleOptionTickerFlag}
                    />
                    <input
                      className="ai-input"
                      type="number"
                      placeholder="Quantity"
                      value={quantity}
                      onChange={handleQuantityChange}
                      step="any"
                      sx={{ width: "100px" }}
                      disabled={isQuantityFieldDisabled}
                    ></input>
                    <button
                      variant="contained"
                      color="primary"
                      onClick={handleAddTicker}
                    >
                      +
                    </button>
                  </div>
                )}
                
                <p className="ai-error-message">{errorMessage}</p>
                </div>
              </>
              )
            )}
            muiTableFooterProps={{
              sx: {
                position: "sticky", // Stick the footer
                bottom: 0, // Stick to the bottom
                zIndex: 1, // Ensure it’s above other content
              },
            }}
          />
        </>
      )}
    </>
  );
}

export default PortfolioDetails;
